import { styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import Skeleton from 'shell/components/Skeleton';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import Card from './Card';

const StudentAssignmentCard = forwardRef(
    (
        {
            assignment,
            hasUnreadInquire = false,
            numAttemptsRemaining,
            onClick,
            onClickGetFeedback,
            onClickInquire,
            onClickViewFeedback,
            ...rest
        },
        ref
    ) => {
        const { t } = useTranslation('assignment');
        const { device } = useViewport();
        const showInquireButton = typeof onClickInquire === 'function';

        const handlePrimaryClick = (e) => {
            e.stopPropagation();
            if (!numAttemptsRemaining) {
                onClickViewFeedback(assignment);
            } else {
                onClickGetFeedback(assignment);
            }
        };

        return (
            <RootBox ref={ref} {...rest}>
                <Card onClick={() => onClick(assignment)}>
                    <RootStack
                        alignItems={'center'}
                        direction={device === 'mobile' ? 'column' : 'row'}
                        justifyContent={'space-between'}
                        spacing={2}
                    >
                        <CustomStack alignItems={'flex-start'} device={device} spacing={2}>
                            {isNaN(numAttemptsRemaining) ? (
                                <Skeleton width={100} />
                            ) : (
                                <Text color={'light'} variant={'p3'}>
                                    {t('attempts-remaining', '{{count}} attempts remaining', {
                                        count: numAttemptsRemaining,
                                    })}
                                </Text>
                            )}

                            <Text align={'left'} noWrap variant={'sh2'}>
                                {assignment.title}
                            </Text>
                        </CustomStack>
                        <ActionsStack device={device} direction={'row'} spacing={2}>
                            {showInquireButton ? (
                                <ActionButton
                                    sx={{ width: 'fit-content' }}
                                    badge={hasUnreadInquire}
                                    device={device}
                                    loading={isNaN(numAttemptsRemaining)}
                                    variant={'secondary'}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onClickInquire(assignment);
                                    }}
                                >
                                    {t('common:controls.inquire', 'Inquire')}
                                </ActionButton>
                            ) : null}
                            <ActionButton
                                device={device}
                                loading={isNaN(numAttemptsRemaining)}
                                onClick={handlePrimaryClick}
                            >
                                {!numAttemptsRemaining
                                    ? t('feedback-section.view-feedback', 'View Feedback')
                                    : t('feedback-section.get-feedback', 'Get Feedback')}
                            </ActionButton>
                        </ActionsStack>
                    </RootStack>
                </Card>
            </RootBox>
        );
    }
);

const RootBox = styled(Box)(() => {
    return {
        width: '100%',
    };
});

const RootStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2),
    };
});

const CustomStack = styled(Stack)(({ device }) => {
    return {
        ...(device === 'mobile'
            ? {
                  width: '100%',
              }
            : {
                  minWidth: 0,
                  maxWidth: '75ch',
              }),
    };
});

const ActionsStack = styled(Stack)(({ device }) => {
    return {
        width: device === 'mobile' ? '100%' : 'auto',
        flexShrink: 0,
    };
});

const ActionButton = styled(Button)(({ device }) => {
    return {
        width: device === 'mobile' ? '100%' : '136px',
    };
});

export default StudentAssignmentCard;
